import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CLOUDFLARE_CONFIG } from "../../config/api";
import { API_CONFIG } from "../../config/api";
import apiService from "../../services/apiService";
import ImageCarousel from "../ImageCarousel";
import * as tus from "tus-js-client";
import "./MediaTab.css";

const MediaTab = () => {
  const { id } = useParams();
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upload status states
  const [videoUploadStatus, setVideoUploadStatus] = useState("");
  const [imageUploadStatus, setImageUploadStatus] = useState("");
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [zipUploadStatus, setZipUploadStatus] = useState("");
  const [isZipUploading, setIsZipUploading] = useState(false);

  // Drag and drop states
  const [draggedRow, setDraggedRow] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Form state variables
  const [videoForm, setVideoForm] = useState({
    file: null,
  });
  const [imageForm, setImageForm] = useState({
    purpose: "",
    files: [], // Changed from 'file' to 'files' array
  });
  const [zipForm, setZipForm] = useState({
    file: null,
  });

  useEffect(() => {
    if (id) {
      fetchMediaItems();
    }
  }, [id]);

  const fetchMediaItems = async () => {
    try {
      setLoading(true);

      const data = await apiService.get(`/property-media-items?propertyId=${id}`);

      // Sort the media items by displayOrder
      const sortedData = data.sort((a, b) => a.displayOrder - b.displayOrder);

      setMediaItems(sortedData);
      setError(null);
    } catch (err) {
      console.error("Error fetching media items:", err);
      setError("Failed to load property media items");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    e.preventDefault();
  
    if (!videoForm.file) {
      setVideoUploadStatus("Please fill in all fields");
      return;
    }
  
    try {
      setIsVideoUploading(true);
      setVideoUploadStatus("Preparing file...");
  
      const originalFile = videoForm.file;
      const newFileName = `CBREX_${id}_${originalFile.name}`;
      const renamedFile = new File([originalFile], newFileName, {
        type: originalFile.type,
      });
  
      setVideoUploadStatus("Getting upload URL from backend...");
  
      // Step 1: Get Cloudflare Stream upload URL from backend
      const uploadUrlResponse = await apiService.post('/Cloudflare/upload-url?uploadType=stream&maxDurationSeconds=3600');
      
      if (!uploadUrlResponse.uploadUrl || !uploadUrlResponse.id) {
        throw new Error("Failed to get upload URL from backend");
      }
  
      setVideoUploadStatus("Uploading to Cloudflare Stream...");
  
      // Step 2: Upload video directly to Cloudflare using the upload URL
      const formData = new FormData();
      formData.append('file', renamedFile);
      
      const cloudflareResponse = await fetch(uploadUrlResponse.uploadUrl, {
        method: 'POST',
        body: formData,
      });
      
      if (!cloudflareResponse.ok) {
        const errorText = await cloudflareResponse.text();
        throw new Error(`Cloudflare upload failed: ${cloudflareResponse.status} - ${errorText}`);
      }
      
      // Cloudflare Stream upload response is typically empty (204 No Content) or minimal
      // Don't try to parse JSON if response is empty
      const contentType = cloudflareResponse.headers.get('content-type');
      let uploadResult = null;
      
      if (contentType && contentType.includes('application/json')) {
        const responseText = await cloudflareResponse.text();
        if (responseText) {
          try {
            uploadResult = JSON.parse(responseText);
          } catch (e) {
            // Response might not be JSON, which is fine
            console.log('Upload response is not JSON, assuming success');
          }
        }
      }
      
      // Use the video ID from the initial direct upload creation response
      const videoId = uploadUrlResponse.id;
      
      setVideoUploadStatus("Upload complete! Saving video to database...");
      
      // Step 3: Construct the watch URL and save to database
      // Format: https://customer-{account}.cloudflarestream.com/{videoId}/watch
      const mediaLink = `${CLOUDFLARE_CONFIG.CFPREFIX}${videoId}/watch`;
      
      await apiService.post('/property-media-items', {
        propertyId: parseInt(id),
        title: "Video",
        fileType: "video",
        mediaLink: mediaLink,
        displayOrder: 100,
      });

      setVideoUploadStatus("Video uploaded successfully!");
      setVideoForm({ file: null });
      fetchMediaItems();

      setTimeout(() => {
        setVideoUploadStatus("");
      }, 3000);
  
    } catch (error) {
      console.error("Error uploading video:", error);
      setVideoUploadStatus("Failed to upload video: " + error.message);
      setIsVideoUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
  
    if (!imageForm.files || imageForm.files.length === 0 || !imageForm.purpose) {
      alert("Please select at least one file and purpose");
      return;
    }
  
    setIsImageUploading(true);
    
    const totalFiles = imageForm.files.length;
    let successCount = 0;
    let failCount = 0;
  
    // Find the highest displayOrder from existing media items
    const maxDisplayOrder = mediaItems.length > 0 
      ? Math.max(...mediaItems.map(item => item.displayOrder || 0))
      : -1;
  
    try {
      // Loop through each file and upload
      for (let i = 0; i < imageForm.files.length; i++) {
        const currentFile = imageForm.files[i];
        setImageUploadStatus(`Uploading image ${i + 1} of ${totalFiles}...`);
  
        try {
          // Step 1: Get Cloudflare upload URL from backend
          setImageUploadStatus(`Getting upload URL for image ${i + 1} of ${totalFiles}...`);
          
          const uploadUrlResponse = await apiService.post('/Cloudflare/upload-url?uploadType=images');
          
          if (!uploadUrlResponse.uploadUrl) {
            throw new Error("Failed to get upload URL from backend");
          }
  
          // Step 2: Upload image directly to Cloudflare using the upload URL
          setImageUploadStatus(`Uploading image ${i + 1} of ${totalFiles} to Cloudflare...`);
          
          const formData = new FormData();
          formData.append('file', currentFile);
          
          const cloudflareResponse = await fetch(uploadUrlResponse.uploadUrl, {
            method: 'POST',
            body: formData,
          });
          
          if (!cloudflareResponse.ok) {
            throw new Error(`Cloudflare upload failed: ${cloudflareResponse.status}`);
          }
          
          const cloudflareResult = await cloudflareResponse.json();
  
          // Step 3: Save to database if Cloudflare upload succeeded
          if (cloudflareResult.success && cloudflareResult.result && cloudflareResult.result.variants) {
            setImageUploadStatus(`Saving image ${i + 1} of ${totalFiles} to database...`);
  
            // Calculate displayOrder: max existing displayOrder + 1 + number of successfully uploaded images in this batch
            const displayOrder = maxDisplayOrder + 1 + successCount;
  
            await apiService.post('/property-media-items', {
              propertyId: parseInt(id),
              title:
                imageForm.purpose === "Play"
                  ? "Play"
                  : imageForm.purpose === "ListImage"
                  ? "ListImage"
                  : imageForm.purpose === "Gallery"
                  ? "Gallery"
                  : "Gallery",
              fileType:
                imageForm.purpose === "Play" ? "videoPlaceholder" : "image",
              mediaLink: cloudflareResult.result.variants[0],
              displayOrder: displayOrder,
            });
  
            successCount++;
          } else {
            throw new Error("Cloudflare upload response missing variants");
          }
        } catch (error) {
          console.error(`Error uploading image ${i + 1}:`, error);
          failCount++;
          // Continue with next file instead of stopping
        }
      }
  
      // Show final status
      if (successCount === totalFiles) {
        setImageUploadStatus(`All ${totalFiles} images uploaded successfully!`);
      } else if (successCount > 0) {
        setImageUploadStatus(`${successCount} of ${totalFiles} images uploaded successfully. ${failCount} failed.`);
      } else {
        setImageUploadStatus(`Failed to upload all images.`);
      }
  
      // Clear form and refresh
      setImageForm({ purpose: "", files: [] });
      fetchMediaItems();
  
      // Clear success message after 5 seconds
      setTimeout(() => {
        setImageUploadStatus("");
      }, 5000);
  
    } catch (error) {
      console.error("Error in batch upload:", error);
      setImageUploadStatus("Failed to upload images: " + error.message);
    } finally {
      setIsImageUploading(false);
    }
  };

  const toggleMedia = async (index) => {
    const item = mediaItems[index];
    
    try {
      // Call the PUT endpoint to toggle isActive
      await apiService.put(`/property-media-items/toggle-is-active?id=${item.id}&userId=3`);

      // Update the local state only if the API call was successful
      setMediaItems((prevItems) =>
        prevItems.map((mediaItem, i) =>
          i === index ? { ...mediaItem, isActive: !mediaItem.isActive } : mediaItem
        )
      );

      console.log(`Media item ${item.id} toggle successful`);
      
    } catch (error) {
      console.error('Error toggling media item:', error);
      alert('Failed to toggle media item. Please try again.');
    }
  };

  const clearVideoForm = () => {
    setVideoForm({
      file: null,
    });
    setVideoUploadStatus("");
  };

  const clearImageForm = () => {
    setImageForm({
      purpose: "",
      files: [],
    });
    setImageUploadStatus("");
  };

  // Add function to handle zip upload
  const handleZipUpload = async (e) => {
    e.preventDefault();

    if (!zipForm.file) {
      setZipUploadStatus("Please select a zip file");
      return;
    }

    // Validate file extension
    const fileName = zipForm.file.name.toLowerCase();
    if (!fileName.endsWith('.zip')) {
      setZipUploadStatus("Please select a file with .zip extension");
      return;
    }

    try {
      setIsZipUploading(true);
      setZipUploadStatus("Uploading zip file...");

      // Create FormData with propertyId and File
      const formData = new FormData();
      formData.append('propertyId', id.toString());
      formData.append('File', zipForm.file, zipForm.file.name);

      await apiService.uploadFile('/property-data-room/upload-media-zip', formData);

      setZipUploadStatus("Zip file uploaded successfully!");
      setZipForm({ file: null });
      fetchMediaItems(); // Refresh the media list

      // Clear success message after 3 seconds
      setTimeout(() => {
        setZipUploadStatus("");
      }, 3000);

    } catch (error) {
      console.error("Error uploading zip file:", error);
      setZipUploadStatus("Failed to upload zip file: " + (error.message || "Please try again"));
    } finally {
      setIsZipUploading(false);
    }
  };

  const clearZipForm = () => {
    setZipForm({
      file: null,
    });
    setZipUploadStatus("");
  };

  // Drag and drop handlers
  const handleDragStart = (e, item, index) => {
    if (item.title !== 'Gallery') return;
    setDraggedRow(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target);
  };

  const handleDragOver = (e, item, index) => {
    if (draggedRow === null || item.title !== 'Gallery') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(index);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, targetItem, targetIndex) => {
    e.preventDefault();
    if (draggedRow === null || targetItem.title !== 'Gallery') return;

    const draggedItem = mediaItems[draggedRow];
    if (draggedItem.title !== 'Gallery') return;

    // Don't do anything if dropping on itself
    if (draggedRow === targetIndex) {
      setDraggedRow(null);
      setDropTarget(null);
      return;
    }

    // Get the row immediately above the drop target
    const rowAbove = targetIndex > 0 ? mediaItems[targetIndex - 1] : null;
    const propertyMediaItemBeforeId = rowAbove ? rowAbove.id : 0;
    
    try {
      // Call API to update display order
      await apiService.put('/property-media-items/re-order', {
        propertyId: parseInt(id),
        propertyMediaItemId: draggedItem.id,
        propertyMediaItemBeforeId: propertyMediaItemBeforeId
      });

      // Refresh the media list
      await fetchMediaItems();

    } catch (error) {
      console.error('Error reordering media item:', error);
      alert('Failed to reorder image. Please try again.');
    }

    setDraggedRow(null);
    setDropTarget(null);
  };

  const handleDragEnd = () => {
    setDraggedRow(null);
    setDropTarget(null);
  };

  // Add function to handle zip file download
  const handleZipDownload = async (item) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/property-data-room/download-media-zip/${item.id}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the filename from the response headers or use a default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${item.title || 'download'}.zip`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create a blob from the response
      const blob = await response.blob();
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`Zip file "${filename}" downloaded successfully`);
      
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download zip file. Please try again.`);
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">Loading media items...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchMediaItems} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <h3>Property Media</h3>

      {/* Media Items Table */}
      <div className="media-table-container">
        <table className="media-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>ID</th>
              <th>Thumbnail</th>
              <th>Title</th>
              <th>File Type</th>
              <th>Display Order</th>
              <th>Media Link</th>
              <th>Publish</th>
            </tr>
          </thead>
          <tbody>
            {mediaItems.map((item, index) => (
              <tr 
                key={item.id}
                draggable={item.title === 'Gallery'}
                onDragStart={(e) => handleDragStart(e, item, index)}
                onDragOver={(e) => handleDragOver(e, item, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, item, index)}
                onDragEnd={handleDragEnd}
                className={`
                  ${item.title === 'Gallery' ? 'draggable-row' : ''}
                  ${draggedRow === index ? 'dragging' : ''}
                  ${dropTarget === index ? 'drop-target' : ''}
                `}
              >
                <td className="drag-handle-cell">
                  {item.title === 'Gallery' && (
                    <div className="drag-handle" title="Drag to reorder">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="6" r="1.5"/>
                        <circle cx="15" cy="6" r="1.5"/>
                        <circle cx="9" cy="12" r="1.5"/>
                        <circle cx="15" cy="12" r="1.5"/>
                        <circle cx="9" cy="18" r="1.5"/>
                        <circle cx="15" cy="18" r="1.5"/>
                      </svg>
                    </div>
                  )}
                </td>
                <td>{item.id}</td>
                <td className="thumbnail-cell">
                  {item.fileType === 'image' && item.mediaLink && (
                    <div className="thumbnail-wrapper">
                      <img 
                        src={item.mediaLink} 
                        alt={item.title}
                        className="thumbnail-image"
                      />
                      <img 
                        src={item.mediaLink} 
                        alt={item.title}
                        className="thumbnail-expanded"
                      />
                    </div>
                  )}
                  {item.fileType === 'video' && (
                    <span className="video-indicator">🎥</span>
                  )}
                </td>
                <td>{item.title}</td>
                <td>{item.fileType}</td>
                <td>{item.displayOrder}</td>
                <td>
                  {item.fileType === 'Zip' ? (
                    <button
                      className="media-link-btn"
                      onClick={() => handleZipDownload(item)}
                      title="Click to download zip file"
                    >
                      Download Zip
                    </button>
                  ) : (
                    item.mediaLink && (
                      <button
                        className="media-link-btn"
                        onClick={() => window.open(item.mediaLink, "_blank")}
                        title="Click to open media in new tab"
                      >
                        🔗 Open Media
                      </button>
                    )
                  )}
                </td>
                <td>
                  {item.fileType !== 'Zip' && (
                    <div className="toggle-container">
                      <div
                        className={`toggle-switch ${item.isActive ? "on" : ""}`}
                        onClick={() => toggleMedia(index)}
                      >
                        <div className="toggle-knob" />
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Image Carousel Display */}
      <ImageCarousel 
        images={mediaItems} 
        propertyId={parseInt(id)}
        onReorder={(result) => {
          console.log('Images reordered:', result);
          if (result.shouldRefresh) {
            fetchMediaItems(); // Refresh the media list
          }
        }}
      />

      {/* Upload Forms Container - Below the Table */}
      <table style={{ border: '1px solid var(--cbre-border)', width: '100%', borderCollapse: 'separate', borderSpacing: '0 16px', marginTop: '16px' }}>
        <tbody>
          <tr style={{ verticalAlign: 'top' }}>
            {/* Left Column - Video Upload */}
            <td style={{ verticalAlign: 'top', width: '50%', paddingRight: '16px' }}>
              <div className="video-upload-form" style={{ verticalAlign: 'top', marginTop: 0 }}>
                <h4>Video Upload</h4>
                <form onSubmit={handleVideoUpload}>
                  <div className="form-row">
                    <label htmlFor="videoFile">Video File:</label>
                    <input
                      type="file"
                      id="videoFile"
                      name="videoFile"
                      accept="video/*"
                      className="form-input"
                      onChange={(e) =>
                        setVideoForm({ ...videoForm, file: e.target.files[0] })
                      }
                      required
                      disabled={isVideoUploading}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isVideoUploading}
                    >
                      {isVideoUploading ? "Uploading..." : "Upload Video"}
                    </button>
                  </div>

                  {/* Upload Status */}
                  {videoUploadStatus && (
                    <div
                      className={`upload-status ${
                        isVideoUploading ? "uploading" : "complete"
                      }`}
                    >
                      {videoUploadStatus}
                    </div>
                  )}
                </form>
              </div>
            </td>

            {/* Right Column - Image Upload */}
            <td style={{ verticalAlign: 'top', width: '50%', paddingLeft: '16px' }}>
              <div className="image-upload-form" style={{ verticalAlign: 'top', marginTop: 0 }}>
                <h4>Image Upload</h4>
                <form onSubmit={handleImageUpload}>
                  <div className="form-row">
                    <label htmlFor="imagePurpose">Image Purpose:</label>
                    <select
                      id="imagePurpose"
                      name="imagePurpose"
                      className="form-select"
                      value={imageForm.purpose}
                      onChange={(e) =>
                        setImageForm({ ...imageForm, purpose: e.target.value })
                      }
                      required
                      disabled={isImageUploading}
                    >
                      <option value="">Select purpose...</option>
                      <option value="Play">Video placeholder</option>
                      <option value="Gallery">Gallery image</option>
                      <option value="ListImage">Listing image</option>
                    </select>
                  </div>

                  <div className="form-row">
                    <label htmlFor="imageFile">Image File:</label>
                    <input
                      type="file"
                      id="imageFile"
                      name="imageFile"
                      accept="image/*"
                      multiple
                      className="form-input"
                      onChange={(e) =>
                        setImageForm({ ...imageForm, files: Array.from(e.target.files) })
                      }
                      required
                      disabled={isImageUploading}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isImageUploading}
                    >
                      {isImageUploading ? "Uploading..." : "Upload Image"}
                    </button>
                  </div>

                  {/* Upload Status */}
                  {imageUploadStatus && (
                    <div
                      className={`upload-status ${
                        isImageUploading ? "uploading" : "complete"
                      }`}
                    >
                      {imageUploadStatus}
                    </div>
                  )}
                </form>
              </div>
            </td>
          </tr>

          <tr style={{ verticalAlign: 'top' }}>
            {/* Left Column - Zip Upload */}
            <td style={{ verticalAlign: 'top', width: '50%', paddingRight: '16px' }}>
              <div className="video-upload-form" style={{ verticalAlign: 'top', marginTop: 0 }}>
                <h4>Zip Upload</h4>
                <form onSubmit={handleZipUpload}>
                  <div className="form-row">
                    <label htmlFor="zipFile">Zip File:</label>
                    <input
                      type="file"
                      id="zipFile"
                      name="zipFile"
                      accept=".zip"
                      className="form-input"
                      onChange={(e) =>
                        setZipForm({ ...zipForm, file: e.target.files[0] })
                      }
                      required
                      disabled={isZipUploading}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isZipUploading || !zipForm.file}
                    >
                      {isZipUploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>

                  {/* Upload Status */}
                  {zipUploadStatus && (
                    <div
                      className={`upload-status ${
                        isZipUploading ? "uploading" : "complete"
                      }`}
                    >
                      {zipUploadStatus}
                    </div>
                  )}
                </form>
              </div>
            </td>

            {/* Right Column - Empty */}
            <td style={{ verticalAlign: 'top', width: '50%', paddingLeft: '16px' }}>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default MediaTab;